import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/* ─────────────────────────────────────────────────────────────────────────────
   ISF Academy LMS — Landing Page
   Style: matches notebuddy.js  (dark bg, terracotta #8c4a22 / #6b3418 accents,
   Tailwind utility classes, no external images)
   RESPONSIVE: fully mobile-first, works on all screen sizes
───────────────────────────────────────────────────────────────────────────── */
function ISFAcademyLMS() {
  const navigate = useNavigate();
  const [hasRefAccess, setHasRefAccess] = useState(false);

  // ── Mirror Navbar refAccess logic (same as EasyLearn) ────────────────────
  useEffect(() => {
    const checkRefAccess = () => {
      try {
        const possibleKeys = ['userData', 'user', 'currentUser', 'authUser', 'loggedUser'];
        let user = null;
        for (const key of possibleKeys) {
          const raw = localStorage.getItem(key);
          if (raw) {
            try { user = JSON.parse(raw); break; } catch {}
          }
        }

        if (user) {
          const role = (user.role || user.userRole || user.user_role || '').toLowerCase();
          const email = (user.email || user.userEmail || '').toLowerCase();
          const adminEmails = ['info@easylearn.net.in', 'info@isfacademy.net.in'];
          const adminRoles = ['super_admin', 'admin', 'teacher', 'staff', 'moderator', 'ref'];

          if (adminEmails.includes(email) || adminRoles.some(r => role.includes(r))) {
            setHasRefAccess(true);
            return;
          }
        }

        if (localStorage.getItem('refAccess') === '1') {
          setHasRefAccess(true);
          return;
        }

        setHasRefAccess(false);
      } catch {
        setHasRefAccess(false);
      }
    };

    checkRefAccess();

    const pollRefAccess = async () => {
      try {
        const userData = localStorage.getItem('userData');
        if (!userData) return;
        const user = JSON.parse(userData);
        if (!user?.email || user.role === 'super_admin' || user.email === 'info@easylearn.net.in') return;
        const res = await fetch(
          `https://dolphin-app-33jp4.ondigitalocean.app/isfathena/user/can-access-reference?email=${encodeURIComponent(user.email)}`
        );
        const data = await res.json();
        const newVal = data.can_access ? '1' : '0';
        if (newVal !== localStorage.getItem('refAccess')) {
          localStorage.setItem('refAccess', newVal);
          setHasRefAccess(data.can_access);
        }
      } catch {}
    };

    const handleStorageEvent = (e) => {
      if (e.key === 'refAccessToggle') {
        try {
          const { email, value } = JSON.parse(e.newValue);
          const userData = localStorage.getItem('userData');
          if (userData) {
            const user = JSON.parse(userData);
            if (user.email === email) {
              localStorage.setItem('refAccess', value);
              setHasRefAccess(value === '1');
            }
          }
        } catch {}
      }
      checkRefAccess();
    };

    const pollInterval = setInterval(pollRefAccess, 10000);
    window.addEventListener('storage', handleStorageEvent);
    window.addEventListener('refAccessChange', checkRefAccess);

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('storage', handleStorageEvent);
      window.removeEventListener('refAccessChange', checkRefAccess);
    };
  }, []);

  /* ── Copy / inspect protection ─────────────────────────────────────────── */
  useEffect(() => {
    const handleContextMenu = (e) => { e.preventDefault(); return false; };
    const handleKeyDown = (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J')) ||
        (e.ctrlKey && e.key === 'U')
      ) { e.preventDefault(); return false; }
    };
    const handleCopy      = (e) => { e.preventDefault(); return false; };
    const handleDragStart = (e) => { e.preventDefault(); return false; };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown',     handleKeyDown);
    document.addEventListener('copy',        handleCopy);
    document.addEventListener('dragstart',   handleDragStart);
    document.body.style.userSelect          = 'none';
    document.body.style.webkitUserSelect    = 'none';
    document.body.style.msUserSelect        = 'none';

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown',     handleKeyDown);
      document.removeEventListener('copy',        handleCopy);
      document.removeEventListener('dragstart',   handleDragStart);
      document.body.style.userSelect          = '';
      document.body.style.webkitUserSelect    = '';
      document.body.style.msUserSelect        = '';
    };
  }, []);

  /* ── Icon helpers ───────────────────────────────────────────────────────── */
  const HeartIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
    </svg>
  );
  const GlobeIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253" />
    </svg>
  );
  const LightbulbIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.355a3.375 3.375 0 01-3 0m1.5-15.25a4.5 4.5 0 100 9 4.5 4.5 0 000-9z" />
    </svg>
  );
  const CloudIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" />
    </svg>
  );
  const ClipboardIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V19.5a2.25 2.25 0 002.25 2.25h.75" />
    </svg>
  );
  const TranslateIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802" />
    </svg>
  );
  const BookOpenIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  );
  const StarIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
    </svg>
  );
  const LibraryIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" />
    </svg>
  );
  const CheckBadgeIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
    </svg>
  );
  const ChartIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  );
  const UsersIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
  const WifiIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z" />
    </svg>
  );
  const TrophyIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0" />
    </svg>
  );
  const BellIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
    </svg>
  );
  const SupportIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
    </svg>
  );
  const ArrowUpIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
    </svg>
  );
  const AcademicCapIcon = () => (
    <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
    </svg>
  );

  /* ── Stat ring component ────────────────────────────────────────────────── */
  const StatRing = ({ pct, label, sublabel }) => {
    const r = 54;
    const circumference = 2 * Math.PI * r;
    const dash = (pct / 100) * circumference;
    return (
      <div className="flex flex-col items-center gap-2 sm:gap-3">
        <div className="relative w-28 h-28 sm:w-36 sm:h-36">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle cx="60" cy="60" r={r} fill="none" stroke="#1f2937" strokeWidth="10" />
            <circle
              cx="60" cy="60" r={r} fill="none"
              stroke="#f3873f" strokeWidth="10"
              strokeDasharray={`${dash} ${circumference}`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xl sm:text-2xl font-extrabold text-white">{pct}%</span>
          </div>
        </div>
        <p className="text-xs sm:text-sm font-bold text-[#f3873f] text-center">{label}</p>
        <p className="text-xs text-gray-400 text-center max-w-[110px] sm:max-w-[130px]">{sublabel}</p>
      </div>
    );
  };

  /* ════════════════════════════════════════════════════════════════════════ */
  return (
    <div className="bg-black text-white min-h-screen overflow-x-hidden">

      <style>{`
        * { box-sizing: border-box; }
        @keyframes twinkle {
          0%, 100% { opacity: 0.15; }
          50%       { opacity: 0.55; }
        }
        .star { animation: twinkle var(--dur, 3s) ease-in-out infinite; }

        /* ── Global responsive fixes ── */
        img { max-width: 100%; height: auto; }

        /* Prevent horizontal scroll */
        .isf-page-root { overflow-x: hidden; }

        /* Ensure hero text never overflows on tiny phones */
        @media (max-width: 360px) {
          .hero-title  { font-size: 1.35rem !important; }
          .hero-sub    { font-size: 0.8rem  !important; }
          .hero-btn    { padding: 8px 16px  !important; font-size: 0.78rem !important; }
        }

        /* CTA buttons: full-width stacked on mobile */
        @media (max-width: 480px) {
          .cta-btn-group { flex-direction: column; }
          .cta-btn-group > button,
          .cta-btn-group > a  { width: 100%; justify-content: center; }
        }

        /* Data insight arrow chevrons: keep icon centred on narrow screens */
        @media (max-width: 640px) {
          .insight-arrow { height: 48px; }
        }

        /* Stat rings: wrap nicely on phone */
        @media (max-width: 480px) {
          .stat-ring-row { gap: 1.5rem; }
        }
      `}</style>

      {/* ── Starfield ───────────────────────────────────────────────────────── */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {[...Array(90)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full star"
            style={{
              width:  Math.random() * 2 + 1 + 'px',
              height: Math.random() * 2 + 1 + 'px',
              top:    Math.random() * 100 + '%',
              left:   Math.random() * 100 + '%',
              '--dur': (Math.random() * 4 + 2) + 's',
              animationDelay: (Math.random() * 5) + 's',
            }}
          />
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-[55vh] sm:min-h-[65vh] lg:min-h-[70vh] flex items-center overflow-hidden">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-center bg-cover bg-no-repeat"
          style={{ backgroundImage: "url('/Assests/home_page_174.png')" }}
        />
        {/* Dark overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, rgba(0,0,0,0.88) 0%, rgba(15,6,2,0.80) 50%, rgba(30,12,3,0.50) 80%, rgba(0,0,0,0.25) 100%)',
          }}
        />

        <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-14 sm:py-20 lg:py-28">
          <div className="max-w-full sm:max-w-[620px]">
            <span
              className="inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-4 sm:mb-5"
              style={{ backgroundColor: '#000000', color: '#fcd9c0' }}
            >
              ISF Academy LMS
            </span>

            <h1
              className="hero-title font-extrabold text-white mb-3 sm:mb-4 leading-tight"
              style={{
                fontSize: 'clamp(1.5rem, 5vw, 2.8rem)',
                textShadow: '0 2px 14px rgba(0,0,0,0.9)',
              }}
            >
              Empowering Bilingual,<br />Values-Driven Learning<br />for the Future
            </h1>

            <p
              className="hero-sub text-gray-300 leading-relaxed mb-6 sm:mb-8"
              style={{
                fontSize: 'clamp(0.82rem, 2.2vw, 1.05rem)',
                maxWidth: '460px',
              }}
            >
              Where Chinese heritage meets global innovation — a seamless digital learning
              platform designed to nurture tomorrow's leaders through bilingual education,
              IB excellence, and timeless values.
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                className="hero-btn text-white rounded-lg font-bold transition-all duration-200 hover:scale-105 shadow-xl hover:opacity-80"
                style={{
                  backgroundColor: '#f3873f',
                  padding: 'clamp(9px,2vw,13px) clamp(18px,4vw,32px)',
                  fontSize: 'clamp(0.78rem, 1.8vw, 0.95rem)',
                }}
              >
                Explore the LMS
              </button>
              <button
                className="hero-btn text-white rounded-lg font-bold transition-all duration-200 hover:opacity-80"
                style={{
                  backgroundColor: '#f3873f',
                  padding: 'clamp(9px,2vw,13px) clamp(18px,4vw,32px)',
                  fontSize: 'clamp(0.78rem, 1.8vw, 0.95rem)',
                }}
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          CHAPTER 1 BANNER — Vision & Philosophy
      ══════════════════════════════════════════════════════════════════════ */}
      <section
        className="relative py-12 sm:py-16 lg:py-20 flex items-center justify-center px-4 sm:px-8"
        style={{ backgroundColor: '#000000' }}
      >
        <div
          className="w-full max-w-3xl mx-auto rounded-2xl p-6 sm:p-8 lg:p-10 relative"
          style={{ backgroundColor: '#2a1508' }}
        >
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
            The ISF Academy Vision and Educational Philosophy
          </h2>
          <p className="text-gray-400 text-sm sm:text-base lg:text-lg leading-relaxed">
            Discover the foundational values and forward-thinking philosophy that guide every aspect of
            ISF Academy's approach to bilingual education and character development.
          </p>
        </div>
      </section>

      {/* ── Rooted in Tradition ─────────────────────────────────────────────── */}
      <section className="bg-black py-14 sm:py-20 lg:py-28 border-t border-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
            Rooted in Tradition, Innovating for Tomorrow
          </h2>
          <p className="text-gray-300 text-sm sm:text-base lg:text-lg leading-relaxed mb-8 sm:mb-10">
            ISF Academy stands at the crossroads of heritage and progress — grounded in time-honored
            virtues while embracing a spirit of bold innovation inspired by Nobel Laureate Professor Charles Kao.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                icon: <HeartIcon />,
                title: 'Eight Virtues + One',
                desc: 'Commitment, respect, benevolence, compassion, courtesy, integrity, harmony, balance, and wisdom form the moral compass of every ISF student.',
              },
              {
                icon: <GlobeIcon />,
                title: 'Bilingual Immersion',
                desc: 'Full immersion in Chinese and English cultivates global perspectives and cross-cultural fluency from the earliest years.',
              },
              {
                icon: <LightbulbIcon />,
                title: 'Innovation Spirit',
                desc: "Inspired by Professor Charles Kao's Nobel-winning legacy, ISF champions curiosity, discovery, and world-shaping vision.",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-xl p-5 sm:p-6 space-y-3 border hover:border-[#f3873f] transition-all duration-200"
                style={{ backgroundColor: '#0f0e0d', borderColor: '#f3873f33' }}
              >
                <div className="flex items-center gap-3">
                  <div className="shrink-0 p-2 rounded-full" style={{ backgroundColor: '#000000', color: 'white' }}>
                    {item.icon}
                  </div>
                  <h3 className="text-sm font-bold" style={{ color: '#f3873f' }}>{item.title}</h3>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bilingual IB World School ────────────────────────────────────────── */}
      <section className="bg-black py-14 sm:py-20 lg:py-28 border-t border-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-start">

          {/* Left column: title + text + orange box */}
          <div className="space-y-5">
            <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-white leading-snug">
              A Unique Bilingual IB World School Experience
            </h2>

            {/* Sub-grid: paragraphs left, key-diff box right — stacked on mobile */}
            <div className="grid grid-cols-1 xs:grid-cols-2 gap-5 items-start">
              {/* Paragraphs */}
              <div className="space-y-4">
                <p className="text-gray-300 text-sm leading-relaxed">
                  As an authorized IB World School, ISF Academy delivers the prestigious Middle Years
                  Programme (MYP) and Diploma Programme (DP), weaving Chinese cultural heritage into
                  every inquiry-based lesson.
                </p>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Through experiential learning and the school's distinctive approach, students develop
                  independent thinking, resilience, and deep character — preparing them to lead with
                  purpose in a complex world.
                </p>
              </div>

              {/* Key Differentiators box */}
              <div
                className="rounded-2xl p-5 space-y-3"
                style={{ backgroundColor: '#000000' }}
              >
                <h3 className="text-sm font-bold text-white mb-3">Key Differentiators</h3>
                {[
                  'IB MYP and Diploma Programme',
                  'Chinese cultural integration',
                  'Inquiry-based pedagogy',
                  'Character-first development',
                  'Experiential learning focus',
                ].map((point) => (
                  <div key={point} className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full bg-white shrink-0 mt-1.5" />
                    <p className="text-white/90 text-sm leading-snug">{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: tall image — hidden on very small screens, shown from sm up */}
          <div
            className="hidden sm:block rounded-2xl overflow-hidden"
            style={{ minHeight: '320px' }}
          >
            <img
              src="/Assests/home_page_175.png"
              alt="ISF Academy students collaborating"
              className="w-full h-full object-cover rounded-2xl"
              style={{ minHeight: '320px', maxHeight: '520px' }}
            />
          </div>

          {/* Mobile: show image below text, smaller */}
          <div
            className="block sm:hidden rounded-2xl overflow-hidden w-full"
            style={{ height: '220px' }}
          >
            <img
              src="/Assests/home_page_175.png"
              alt="ISF Academy students collaborating"
              className="w-full h-full object-cover rounded-2xl"
            />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          CHAPTER 2 BANNER — LMS Platform Overview
      ══════════════════════════════════════════════════════════════════════ */}
      <section
        className="relative py-12 sm:py-16 lg:py-20 flex items-center justify-center px-4 sm:px-8"
        style={{ backgroundColor: '#000000' }}
      >
        <div
          className="w-full max-w-3xl mx-auto rounded-2xl p-6 sm:p-8 lg:p-10"
          style={{ backgroundColor: '#2a1508' }}
        >
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
            The ISF Academy LMS Platform Overview
          </h2>
          <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
            Explore the centralized digital ecosystem that powers curriculum delivery, student
            engagement, and seamless communication across the ISF Academy community.
          </p>
        </div>
      </section>

      {/* ── Seamless Digital Learning Environment ───────────────────────────── */}
      <section className="bg-black py-14 sm:py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
            Seamless Digital Learning Environment
          </h2>
          <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-7 sm:mb-8">
            The ISF Academy LMS, accessible at{' '}
            <span className="font-semibold underline" style={{ color: '#f3873f' }}>isfacademy.net.in</span>,
            serves as the central hub for all teaching and learning activities — bringing together
            curriculum content, assessments, and progress tracking in one intuitive platform.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6">
            {[
              {
                icon: <CloudIcon />,
                title: 'Centralized Access',
                desc: 'One unified platform for all curriculum materials, resources, and communications — available anytime, anywhere.',
              },
              {
                icon: <ClipboardIcon />,
                title: 'Assessments & Tracking',
                desc: "Comprehensive tools for creating assessments, grading, and monitoring each student's academic journey in real time.",
              },
              {
                icon: <TranslateIcon />,
                title: 'Bilingual K-12 Content',
                desc: 'Designed specifically for K-12 learners with rich bilingual resources in both Chinese and English.',
              },
            ].map((item) => (
              <div key={item.title} className="flex flex-col gap-2">
                <div className="flex items-center gap-2 mb-1">
                  <div className="shrink-0" style={{ color: '#f3873f' }}>{item.icon}</div>
                  <h3 className="text-sm font-bold text-white">{item.title}</h3>
                </div>
                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── User-Friendly Interface ──────────────────────────────────────────── */}
      <section className="py-14 sm:py-20 lg:py-28 bg-black">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-8 sm:mb-10 leading-snug">
            User-Friendly Interface for Students and Educators
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6">
            {[
              {
                img: '/Assests/home_page_176.png',
                title: 'Student Dashboards',
                desc: 'Personalized portals where students access courses, submit assignments, and receive timely feedback on their progress.',
              },
              {
                img: '/Assests/home_page_177.png',
                title: 'Teacher Tools',
                desc: 'Educators create interactive lessons, design quizzes, and monitor student engagement with powerful built-in analytics.',
              },
              {
                img: '/Assests/home_page_178.png',
                title: 'Parent Visibility',
                desc: 'Parents stay informed with transparent access to student progress, school communications, and upcoming events.',
              },
            ].map((item) => (
              <div key={item.title} className="flex flex-col gap-3">
                {/* Thumbnail image */}
                <div className="rounded-xl overflow-hidden" style={{ height: '140px' }}>
                  <img
                    src={item.img}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-sm font-bold text-white">{item.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          CHAPTER 3 BANNER — Curriculum Integration
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative bg-black py-14 sm:py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
            Curriculum Integration and Learning Resources
          </h2>
          <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
            See how the LMS brings the IB curriculum to life through digital tools, bilingual resources,
            and structured reading programs that inspire a lifelong love of learning.
          </p>
        </div>
      </section>

      {/* ── Supporting the IB Curriculum ────────────────────────────────────── */}
      <section className="py-14 sm:py-20 lg:py-28 bg-black">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-3">
            Supporting the IB Curriculum with Digital Tools
          </h2>
          <p className="text-white/80 text-sm sm:text-base leading-relaxed mb-8 sm:mb-10">
            The LMS is purpose-built to align with IB MYP and Diploma Programme frameworks, ensuring
            every digital interaction supports curriculum coherence and academic rigor.
          </p>
          {/* Cards need extra top padding on mobile to show the badge */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6 pt-4">
            {[
              {
                num: '1',
                title: 'IB Framework Alignment',
                desc: 'Every module maps directly to MYP and DP standards, maintaining seamless curriculum coherence across subjects.',
              },
              {
                num: '2',
                title: 'Shuyuan Model',
                desc: "Project-based and interdisciplinary studies facilitated through ISF's unique Shuyuan learning model.",
              },
              {
                num: '3',
                title: 'Bilingual Resources',
                desc: 'Extensive library of eBooks, multimedia, and interactive materials available in both Chinese and English.',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-xl p-5 sm:p-6 pt-8 space-y-2 relative mt-4"
                style={{ backgroundColor: '#000000' }}
              >
                {/* Numbered circle badge at top-center */}
                <div
                  className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center text-xs font-extrabold text-white shadow-lg"
                  style={{ backgroundColor: '#000000', border: '2px solid #f3873f' }}
                >
                  {item.num}
                </div>
                <h3 className="text-sm font-bold text-white mt-2">{item.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Reading & Language Mastery ───────────────────────────────────────── */}
      <section className="bg-black py-14 sm:py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-start">
          {/* Left: text */}
          <div className="space-y-4 sm:space-y-5">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white leading-snug">
              Encouraging Reading and Language Mastery
            </h2>
            <p className="text-gray-300 text-sm leading-relaxed">
              Reading lies at the heart of ISF's educational philosophy. The LMS integrates structured
              reading programs — including Accelerated Reader and Star Reading — directly into the
              student experience.
            </p>
            <p className="text-gray-400 text-sm leading-relaxed">
              Library lessons are embedded into the weekly LMS schedule, promoting literacy and
              cultivating a genuine love of reading. Online quizzes and assessments continuously
              monitor comprehension and track growth over time.
            </p>
          </div>
          {/* Right: vertical button list */}
          <div className="flex flex-col gap-3">
            {[
              { icon: <BookOpenIcon />, label: 'Accelerated Reader' },
              { icon: <StarIcon />,     label: 'Star Reading' },
              { icon: <LibraryIcon />,  label: 'Library Lessons' },
              { icon: <CheckBadgeIcon />, label: 'Online Assessments' },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-3 sm:gap-4 rounded-xl px-4 sm:px-5 py-3 sm:py-4 w-full"
                style={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                <div className="shrink-0" style={{ color: '#f3873f' }}>{item.icon}</div>
                <span className="text-sm font-semibold text-white">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          CHAPTER 4 BANNER — Engagement & Assessment
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative bg-black py-14 sm:py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center">
          {/* Left: title + desc */}
          <div className="space-y-4 sm:space-y-5">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white leading-tight">
              Enhancing Student Engagement and Assessment
            </h2>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed max-w-md">
              From real-time feedback to data-driven personalization, discover how the ISF LMS transforms
              assessment into a powerful engine for student growth.
            </p>
          </div>
          {/* Right: image */}
          <div className="rounded-2xl overflow-hidden" style={{ minHeight: '220px' }}>
            <img
              src="/Assests/home_page_179.png"
              alt="Student Engagement"
              className="w-full h-full object-cover rounded-2xl"
              style={{ minHeight: '220px', maxHeight: '360px' }}
            />
          </div>
        </div>
      </section>

      {/* ── Interactive Learning & Feedback ─────────────────────────────────── */}
      <section className="bg-black py-14 sm:py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          <h2
            className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-8 sm:mb-10 pl-4"
            style={{ borderLeft: '4px solid #f3873f' }}
          >
            Interactive Learning and Continuous Feedback
          </h2>

          {/* 2x2 Grid — single column on mobile, 2 columns from sm */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {[
              {
                icon: (
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                ),
                label: 'Real-Time Quizzes',
                desc: 'Live quizzes with instant grading keep students engaged and on track.',
              },
              {
                icon: (
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                ),
                label: 'Instant Feedback',
                desc: 'Students receive immediate, actionable feedback to guide their learning.',
              },
              {
                icon: (
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                ),
                label: 'Group Projects',
                desc: 'Collaborative tools foster teamwork and shared achievement across classrooms.',
              },
              {
                icon: (
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                ),
                label: 'Gamified Rewards',
                desc: 'Points, badges, and leaderboards motivate students to reach their full potential.',
              },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-start gap-3 sm:gap-4 rounded-xl p-4 sm:p-5"
                style={{ backgroundColor: '#0f0e0d', border: '1px solid rgba(255,255,255,0.10)' }}
              >
                {/* Orange icon badge */}
                <div
                  className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center mt-0.5"
                  style={{ backgroundColor: '#f3873f' }}
                >
                  <div className="text-white">{item.icon}</div>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white mb-1">{item.label}</h3>
                  <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Data-Driven Insights ─────────────────────────────────────────────── */}
      <section className="py-14 sm:py-20 lg:py-28 bg-black">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-8 sm:mb-10">
            Monitoring Progress with Data-Driven Insights
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                icon: <ChartIcon />,
                title: 'Detailed Analytics',
                desc: 'Teachers and administrators access granular data on student performance across subjects and assessments.',
              },
              {
                icon: <CheckBadgeIcon />,
                title: 'Personalized Pathways',
                desc: "Learning pathways adapt based on assessment data and each student's Zone of Proximal Development (ZPD).",
              },
              {
                icon: <WifiIcon />,
                title: 'Hybrid & Remote Ready',
                desc: 'Secure access and flexible scheduling support seamless hybrid and remote learning experiences.',
              },
            ].map((item) => (
              <div key={item.title} className="flex flex-col">
                {/* Arrow / chevron badge */}
                <div className="insight-arrow relative mb-4" style={{ height: '52px' }}>
                  <svg viewBox="0 0 220 56" className="w-full h-full" preserveAspectRatio="none">
                    <path d="M0,0 L190,0 L220,28 L190,56 L0,56 Z" fill="#f3873f"/>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center" style={{ color: '#b85a22' }}>
                    {item.icon}
                  </div>
                </div>
                <h3 className="text-sm font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          CHAPTER 5 BANNER — Community
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative bg-black py-14 sm:py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center">
          {/* Left: title + desc */}
          <div className="space-y-4 sm:space-y-5">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white leading-tight">
              Community, Support, and Future Growth
            </h2>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed max-w-md">
              The ISF Academy LMS extends beyond the classroom — connecting students, parents, faculty,
              and alumni while continuously evolving to meet the needs of a 21st-century learning community.
            </p>
          </div>
          {/* Right: image */}
          <div className="rounded-2xl overflow-hidden" style={{ minHeight: '220px' }}>
            <img
              src="/Assests/home_page_180.png"
              alt="ISF Community"
              className="w-full h-full object-cover rounded-2xl"
              style={{ minHeight: '220px', maxHeight: '380px' }}
            />
          </div>
        </div>
      </section>

      {/* ── Connected ISF Community ──────────────────────────────────────────── */}
      <section className="py-14 sm:py-20 lg:py-28 bg-black">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-8 sm:mb-10">
            Building a Connected ISF Community
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              {
                img: '/Assests/home_page_181.png',
                title: 'Portal Integration',
                desc: 'A unified portal connects students, parents, faculty, and alumni — fostering engagement and a strong sense of belonging.',
              },
              {
                img: '/Assests/home_page_182.png',
                title: 'LMS Announcements',
                desc: 'Regular updates on achievements, events, and school news keep the entire community informed and celebrated.',
              },
              {
                img: '/Assests/home_page_183.png',
                title: 'Support Services',
                desc: 'Comprehensive technical help, counseling, and academic guidance ensure every user gets the support they need.',
              },
            ].map((item) => (
              <div key={item.title} className="flex flex-col gap-3">
                {/* Circular image thumbnail */}
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden shrink-0 border-2 border-black/20">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Continuous Improvement ───────────────────────────────────────────── */}
      <section className="py-14 sm:py-20 lg:py-28 relative overflow-hidden">
        {/* Background image with dark overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="/Assests/home_page_136.png"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.78)' }} />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-8 sm:mb-10">
            Commitment to Continuous Improvement and Innovation
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-start">
            {/* Our Promise card */}
            <div
              className="rounded-2xl p-6 sm:p-8 h-full"
              style={{ backgroundColor: 'rgba(0,0,0,0.18)', border: '1px solid rgba(255,255,255,0.20)' }}
            >
              <h3 className="text-base font-bold text-white mb-4">Our Promise</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                ISF Academy is dedicated to staying at the forefront of educational technology —
                ensuring the LMS grows alongside the needs of its learners and educators.
              </p>
            </div>
            {/* Three pillars */}
            <div className="space-y-0">
              {[
                { num: '01', title: 'Platform Evolution',    desc: 'Ongoing updates incorporate the latest educational technologies and user feedback.' },
                { num: '02', title: 'Educator Development',  desc: 'Professional development programs help teachers maximize LMS effectiveness in the classroom.' },
                { num: '03', title: 'Global Expansion',      desc: 'A bold vision for expanding experiential learning and international collaborations.' },
              ].map((item) => (
                <div key={item.num} className="pt-4 pb-4" style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                  <p className="text-xs text-gray-500 mb-1 font-mono tracking-widest">{item.num}</p>
                  <h3 className="text-sm font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          CHAPTER 6 BANNER — Real-World Impact
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative bg-black py-14 sm:py-20 lg:py-28 border-t border-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
            Real-World Impact and Success Stories
          </h2>
          <p className="text-gray-400 text-sm sm:text-base lg:text-lg leading-relaxed">
            The true measure of any learning platform lies in its outcomes. Explore the tangible
            achievements and inspiring stories that demonstrate the ISF Academy LMS in action.
          </p>
        </div>
      </section>

      {/* ── Student Achievements ─────────────────────────────────────────────── */}
      <section className="bg-black py-14 sm:py-20 lg:py-28 border-t border-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-white mb-8 sm:mb-10">
            Student Achievements Powered by ISF LMS
          </h2>

          {/* Stat rings — centred on all sizes */}
          <div className="stat-ring-row flex flex-wrap justify-center gap-6 sm:gap-10 mb-10 sm:mb-12">
            <StatRing pct={92} label="Literacy Growth"  sublabel="Students meeting or exceeding reading benchmarks" />
            <StatRing pct={85} label="IB Pass Rate"     sublabel="Diploma Programme students achieving strong results" />
            <StatRing pct={78} label="Engagement Rate"  sublabel="Active daily LMS participation across all grade levels" />
          </div>

          {/* Quote */}
          <blockquote
            className="border-l-4 pl-4 sm:pl-5 py-3 rounded-r-xl mb-8 max-w-2xl"
            style={{ borderColor: '#f3873f', backgroundColor: '#0f0f0f' }}
          >
            <p className="text-gray-200 text-sm sm:text-base leading-relaxed italic">
              "The LMS transformed how I study — I can access everything I need, track my progress,
              and collaborate with classmates anytime."
            </p>
            <cite className="text-xs font-bold mt-2 block" style={{ color: '#f3873f' }}>— ISF Student</cite>
          </blockquote>

          <p className="text-gray-400 text-sm sm:text-base leading-relaxed">
            Case studies reveal that project-based learning facilitated through the platform has led to
            measurably improved outcomes, with both students and teachers reporting enhanced learning experiences.
          </p>
        </div>
      </section>

      {/* ── Preparing Tomorrow's Leaders + Final CTA ────────────────────────── */}
      <section className="py-14 sm:py-20 lg:py-28 bg-black">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">

          {/* Preparing Tomorrow card */}
          <div
            className="rounded-2xl p-6 sm:p-8 lg:p-10 mb-12 sm:mb-16"
            style={{ backgroundColor: '#2a1508' }}
          >
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-5 sm:mb-6 text-center">
              Preparing Tomorrow's Leaders Today
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              {[
                {
                  title: 'Critical Thinking & Creativity',
                  desc: 'The ISF LMS nurtures higher-order thinking skills through inquiry-based modules, open-ended challenges, and creative projects that push students beyond memorization.',
                },
                {
                  title: 'Character & Values',
                  desc: 'Grounded in the Eight Virtues + One, every learning experience reinforces integrity, compassion, and respect — shaping not just scholars, but principled leaders.',
                },
                {
                  title: 'Global Impact',
                  desc: 'ISF alumni are making meaningful contributions worldwide, inspired by the values and bilingual education model that defined their formative years.',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="rounded-xl p-5 sm:p-6 space-y-3"
                  style={{ backgroundColor: '#1a0c05', border: '1px solid rgba(255,255,255,0.12)' }}
                >
                  <h3 className="text-sm font-bold text-white text-center">{item.title}</h3>
                  <p className="text-gray-300 text-xs leading-relaxed text-center">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-white mb-4 sm:mb-5">
            Join the Digital Education Revolution with ISF Academy LMS
          </h2>
          <p className="text-gray-400 text-sm sm:text-base mb-8 sm:mb-10">
            The future of bilingual, values-driven education is here. Visit{' '}
            <a
              href="https://isfacademy.net.in"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold hover:underline"
              style={{ color: '#f3873f' }}
            >
              isfacademy.net.in
            </a>{' '}
            to discover how our LMS can transform your institution's learning journey.
          </p>

          {/* Cards Grid — stacked on mobile, 2-col from md */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-10">
            <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-white rounded-2xl p-6 sm:p-8 hover:border-[#f3873f] transition-all duration-200">
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">Transform Today</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Empower your educators with cutting-edge bilingual tools that enhance rather than complicate their teaching — rooted in IB excellence and timeless values.
              </p>
            </div>
            <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-white rounded-2xl p-6 sm:p-8 hover:border-[#f3873f] transition-all duration-200">
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">Engage Tomorrow</h3>
              <p className="text-gray-300 text-sm leading-relaxed">
                Give students the interactive, bilingual learning experience they need to succeed — blending Chinese heritage with global innovation for the leaders of tomorrow.
              </p>
            </div>
          </div>

          {/* CTA Buttons — full width stacked on mobile, inline from sm */}
          <div className="cta-btn-group flex flex-wrap gap-3 sm:gap-4">
            {hasRefAccess && (
              <button
                onClick={() => navigate('/reffrence?productId=5')}
                className="px-6 sm:px-8 py-3 sm:py-4 text-white text-base sm:text-lg font-semibold rounded-lg transition-all duration-200 hover:opacity-80 flex items-center gap-2"
                style={{ backgroundColor: '#f3873f' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Reference
              </button>
            )}
            <button
              className="px-6 sm:px-8 py-3 sm:py-4 text-white text-base sm:text-lg font-semibold rounded-lg transition-all duration-200 hover:opacity-80"
              style={{ backgroundColor: '#f3873f' }}
            >
              Start Your Journey
            </button>
            <button
              className="px-6 sm:px-8 py-3 sm:py-4 text-white text-base sm:text-lg font-semibold rounded-lg transition-all duration-200 hover:opacity-80"
              style={{ backgroundColor: '#f3873f' }}
            >
              Schedule Demo
            </button>
          </div>

        </div>
      </section>

    </div>
  );
}

export default ISFAcademyLMS;